import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useTheme } from "@/lib/theme";

/**
 * Site-wide footer. Theme-aware (cream / near-black), responsive
 * (single-column below md, 4-column above), and Nordic MarTech branded.
 */
export default function AppFooter() {
  const { theme } = useTheme();
  const dark = theme === "dark";

  const nav = [
    { label: "How it works", to: "/how-it-works", testid: "footer-how-link" },
    { label: "Benchmarks", to: "/benchmarks", testid: "footer-benchmarks-link" },
    { label: "For CMOs", to: "/for-cmos", testid: "footer-for-cmos-link" },
    { label: "Show your CFO", to: "/convince-your-cfo", testid: "footer-for-finance-link" },
    { label: "Pricing", to: "/pricing", testid: "footer-pricing-link" },
    { label: "Compare", to: "/compare", testid: "footer-compare-link" },
  ];

  const legal = [
    { label: "Privacy", to: "/privacy", testid: "footer-privacy-link" },
    { label: "Terms", to: "/terms", testid: "footer-terms-link" },
    { label: "Imprint", to: "/imprint", testid: "footer-imprint-link" },
  ];

  const bg = dark ? "bg-[#0A0A0A]" : "bg-[#FFFDF6]";
  const border = dark ? "border-white/10" : "border-slate-200";
  const brandText = dark ? "text-slate-100" : "text-slate-900";
  const linkColor = dark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-slate-900";
  const mutedText = dark ? "text-slate-500" : "text-slate-500";
  const dim = dark ? "text-slate-500" : "text-slate-400";

  return (
    <footer className={`border-t ${border} ${bg} no-print`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 items-start">
          {/* Brand block */}
          <div className="md:col-span-5 flex items-start gap-4">
            <Logo size="lg" inverse={dark} />
            <div>
              <p className={`text-sm uppercase tracking-[0.28em] font-semibold ${brandText}`}>
                PressImpact
              </p>
              <p className={`text-xs ${mutedText} leading-relaxed mt-2 max-w-xs`}>
                Transparent press release ROI intelligence for modern teams.
                Nordic-built. Live EUR / NOK / USD.
              </p>
              <p
                data-testid="footer-legal-entity"
                className={`text-[10px] tracking-[0.24em] uppercase font-semibold mt-4 ${dim}`}
              >
                Kaada Nordic MarTech · Org nr. 936&nbsp;289&nbsp;835
              </p>
            </div>
          </div>

          {/* Product nav */}
          <div className="md:col-span-4">
            <p className={`text-[10px] tracking-[0.28em] uppercase font-semibold ${dim} mb-4`}>
              Product
            </p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {nav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={item.testid}
                    className={`text-xs tracking-wide font-medium transition-colors ${linkColor}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal + Trust */}
          <div className="md:col-span-3 flex flex-col gap-6">
            <div>
              <p className={`text-[10px] tracking-[0.28em] uppercase font-semibold ${dim} mb-4`}>
                Legal
              </p>
              <ul className="space-y-2">
                {legal.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      data-testid={item.testid}
                      className={`text-xs tracking-wide font-medium transition-colors ${linkColor}`}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className={`text-[10px] tracking-[0.28em] uppercase font-semibold ${dim} mb-4`}>
                Trust
              </p>
              <ul className="space-y-1.5">
                <li className={`text-xs ${linkColor}`}>ECB-backed FX</li>
                <li className={`text-xs ${linkColor}`}>GDPR-clean · EU-hosted</li>
                <li className={`text-xs ${linkColor}`}>EN + SV UI</li>
              </ul>
            </div>
          </div>
        </div>

        <div className={`mt-10 pt-6 border-t ${border} flex flex-col md:flex-row items-start md:items-center justify-between gap-3`}>
          <span
            data-testid="app-footer-copyright"
            className={`text-xs tracking-wide ${mutedText}`}
          >
            © {new Date().getFullYear()} Kaada Nordic MarTech (Org nr. 936&nbsp;289&nbsp;835). All rights reserved.
          </span>
          <span className={`text-[10px] tracking-[0.28em] uppercase font-semibold ${dim}`}>
            v1 · Feb 2026 · Oslo
          </span>
        </div>
      </div>
    </footer>
  );
}
